import type { SurveyDraft } from '@/state/surveys/useSurvey';
import type { DraftQuestion } from '@/state/surveys/survey-questions';
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'helpers/cn';
import { SURVEY_DEFAULT_TEXTS, SURVEY_SCALE_END_LABELS, SURVEY_SCORE_RANGES } from 'definitions';
import { optionsOf, scaleFor } from '@/state/surveys/survey-questions';

type SurveyPreviewProps = { draft: SurveyDraft };

type QuestionPreviewProps = { question: DraftQuestion; type: SurveyDraft['type']; accent: string };

const PENNANTS = ['#24CBC7', '#3B60D9', '#7C5CFF'];

const QuestionPreview = ({ question, type, accent }: QuestionPreviewProps) => {
  if (question.kind === 'score') {
    const scale = scaleFor(question, type);
    const { min, max } = SURVEY_SCORE_RANGES[scale];
    const [lowDefault, highDefault] = SURVEY_SCALE_END_LABELS[scale];
    const values = Array.from({ length: max - min + 1 }, (_, index) => min + index);
    return (
      <>
        {scale === 'CSAT' ? (
          <div className="mt-3 flex gap-1.5 text-2xl leading-none" style={{ color: accent }}>
            {values.map((value) => (
              <span key={value}>★</span>
            ))}
          </div>
        ) : (
          <div className="mt-3 flex gap-1">
            {values.map((value) => (
              <span
                key={value}
                className="flex h-8 flex-1 items-center justify-center rounded border border-black/10 text-micro text-gray-700"
              >
                {value}
              </span>
            ))}
          </div>
        )}
        <div className="mt-1.5 flex justify-between gap-3 text-nano text-gray-400">
          <span>{String(question.config?.lowLabel ?? '') || lowDefault}</span>
          <span>{String(question.config?.highLabel ?? '') || highDefault}</span>
        </div>
      </>
    );
  }

  if (question.kind === 'open text') {
    return (
      <div className="mt-2.5 h-14 rounded border border-black/10 p-2 text-micro text-gray-400">
        {String(question.config?.placeholder ?? '')}
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-1.5">
      {optionsOf(question).map((option, index) => (
        <span key={index} className="rounded border border-black/15 px-2.5 py-1.5 text-micro text-gray-700">
          {option || `Option ${index + 1}`}
        </span>
      ))}
    </div>
  );
};

/**
 * A React representation of the widget, not the widget itself.
 *
 * The shipped widget is vanilla TS in runtime-surveys, deliberately, for bundle
 * size — it cannot be imported here. So this markup is a second implementation
 * and can drift from what actually ships. The alternative was an iframe loading
 * the real bundle over postMessage, which is the architecturally correct answer
 * and what injector does for the editor, but a lot of machinery for a preview of
 * a small card.
 *
 * What keeps the drift bounded: the scale bounds, the default end labels and the
 * default texts all come from definitions, which the bundle generator also reads,
 * so the two cannot disagree about the thing customers would actually notice.
 * Like the widget it shows one question at a time; the arrows under it step
 * through them.
 *
 * The greys and the accent below are deliberately NOT console tokens, and this is
 * the one file in the dashboard where that is correct. Everything inside the
 * preview frame is a picture of what a visitor sees on the customer's own site:
 * it is always light, and its accent is the customer's chosen colour, so it must
 * not follow our theme. Retokenising this would make the preview lie in dark
 * mode. The console styling stops at the frame around it.
 */
export const SurveyPreview = ({ draft }: SurveyPreviewProps) => {
  const [requested, setRequested] = useState(0);
  const count = draft.questions.length;
  // Clamped on read, so removing the question being previewed cannot leave the
  // index pointing past the end.
  const index = Math.min(requested, Math.max(count - 1, 0));
  const question = draft.questions[index];
  const isLast = index >= count - 1;

  const accent = /^#[0-9a-fA-F]{6}$/.test(draft.theme.accentColor ?? '') ? draft.theme.accentColor : '#2563eb';
  const radius = draft.theme.borderRadius ?? 12;
  const texts = { ...SURVEY_DEFAULT_TEXTS, ...draft.texts };

  /*
    Only the stage is console chrome. Everything inside the card — the white
    surface, the greys, the accent — is a simulation of the customer's own page
    and their own theme choice, so it stays literal and is deliberately NOT
    mapped onto the console tokens.
  */
  return (
    <div className="space-y-2">
      <div className="bg-field border-line rounded-card relative flex min-h-[280px] items-end justify-center border p-4">
        {draft.position === 'center' && <div className="absolute inset-0 rounded-card bg-black/20" />}
        <div
          className={cn(
            'relative w-full max-w-[300px] bg-white p-4 text-body shadow-lg',
            draft.position === 'bottom right' && 'ml-auto mr-0',
            draft.position === 'bottom left' && 'ml-0 mr-auto',
            draft.position === 'bottom center' && 'mx-auto',
            draft.position === 'center' && 'mx-auto mb-auto mt-auto',
          )}
          style={{ borderRadius: `${radius}px` }}
        >
          <p className="pr-4 font-semibold text-gray-900">{question?.label || 'Your question here'}</p>
          {question && <QuestionPreview question={question} type={draft.type} accent={accent ?? '#2563eb'} />}

          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-nano text-gray-400">
              <svg viewBox="0 0 20 24" className="h-3 w-2.5" aria-hidden="true">
                {PENNANTS.map((fill, pennant) => (
                  <rect key={fill} x={pennant * 7.5} width="5" height="24" rx="2.5" fill={fill} />
                ))}
              </svg>
              Powered by Pertento
            </span>
            <span className="flex items-center gap-2">
              {count > 1 && (
                <span className="text-nano text-gray-400">
                  {index + 1}/{count}
                </span>
              )}
              <span className="rounded px-3 py-1.5 text-micro font-medium text-white" style={{ background: accent }}>
                {isLast ? texts.submit : texts.next}
              </span>
            </span>
          </div>
        </div>
      </div>

      {count > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setRequested(Math.max(index - 1, 0))}
            disabled={index === 0}
            className="text-dim hover:text-foreground hover:bg-hover rounded-lg p-1 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous question</span>
          </button>
          <span className="micro-label">
            Question {index + 1} of {count}
          </span>
          <button
            type="button"
            onClick={() => setRequested(Math.min(index + 1, count - 1))}
            disabled={isLast}
            className="text-dim hover:text-foreground hover:bg-hover rounded-lg p-1 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next question</span>
          </button>
        </div>
      )}
    </div>
  );
};
